import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpContext } from '@angular/common/http';
import { backendEndPointToken, terminologyCanonicalUrlBaseToken } from '../app.config';
import { Observable, forkJoin, map, of, catchError } from 'rxjs';
import { Bundle } from 'fhir/r4';
import { AuthService } from '../shared/auth/auth.service';
import { UtilityService } from '../shared/utility.service';
import { LoadingUIEnabled } from '../loading.interceptor';

type DateSearchRange = {
    start: string;
    end: string;
    appointmentFilter: string;
    encounterFilter: string;
    patientFilter: string;
};

type OwnedSharedCount = {
    owned: number;
    shared: number;
};

type AppointmentStatusCounts = {
    proposed: OwnedSharedCount;
    fulfilled: OwnedSharedCount;
    noShow: OwnedSharedCount;
    booked: OwnedSharedCount;
    pending: OwnedSharedCount;
    waitlist: OwnedSharedCount;
};

export interface DashboardMetrics {
    appointments: {
        total: number;
        fulfilled: number;
        noShow: number;
        proposed: number;
        booked: number;
        pending: number;
        waitlist: number;
        remaining: number;
        // Current status (not time-filtered)
        currentProposed: number;
        currentBooked: number;
        currentPending: number;
        currentWaitlist: number;
    };
    encounters: {
        total: number;
        inpatient: number;
        outpatient: number;
        emergency: number;
        virtual: number;
        home: number;
    };
    patients: {
        total: number;
    };
    resources: {
        bedsAvailable: number;
        bedsTotal: number;
        outOfStockMedicines: number;
        totalMedicines: number;
    };
}

@Injectable({
    providedIn: 'root'
})
export class AdminDashboardService {
    private http = inject(HttpClient);
    private backendUrl = inject(backendEndPointToken);
    private terminologyBaseUrl = inject(terminologyCanonicalUrlBaseToken);
    private auth = inject(AuthService);
    private utilityService = inject(UtilityService); // Inject UtilityService

    private get tenantTagSystem() {
        return `${this.terminologyBaseUrl}/CodeSystem/organizationTenant`;
    }

    getMetrics(timeRange: string = 'Today'): Observable<DashboardMetrics> {
        const orgId = this.auth.user.getValue()?.organizationId;
        if (!orgId) return of(this.getEmptyMetrics());

        const context = new HttpContext().set(LoadingUIEnabled, false);
        const dateRange = this.getDateSearchRange(timeRange);

        const apptQueries = {
            owned: this.getCount(`Appointment?_tag=${orgId}${dateRange.appointmentFilter}`, context),
            shared: this.getCount(`Appointment?_security=${orgId}${dateRange.appointmentFilter}`, context),
        };

        const statusCountQueries: Record<keyof AppointmentStatusCounts, Observable<OwnedSharedCount>> = {
            proposed: forkJoin({
                owned: this.getCount(`Appointment?_tag=${orgId}${dateRange.appointmentFilter}&status=proposed`, context),
                shared: this.getCount(`Appointment?_security=${orgId}${dateRange.appointmentFilter}&status=proposed`, context),
            }),
            fulfilled: forkJoin({
                owned: this.getCount(`Appointment?_tag=${orgId}${dateRange.appointmentFilter}&status=fulfilled`, context),
                shared: this.getCount(`Appointment?_security=${orgId}${dateRange.appointmentFilter}&status=fulfilled`, context),
            }),
            noShow: forkJoin({
                owned: this.getCount(`Appointment?_tag=${orgId}${dateRange.appointmentFilter}&status=noshow`, context),
                shared: this.getCount(`Appointment?_security=${orgId}${dateRange.appointmentFilter}&status=noshow`, context),
            }),
            booked: forkJoin({
                owned: this.getCount(`Appointment?_tag=${orgId}${dateRange.appointmentFilter}&status=booked`, context),
                shared: this.getCount(`Appointment?_security=${orgId}${dateRange.appointmentFilter}&status=booked`, context),
            }),
            pending: forkJoin({
                owned: this.getCount(`Appointment?_tag=${orgId}${dateRange.appointmentFilter}&status=pending`, context),
                shared: this.getCount(`Appointment?_security=${orgId}${dateRange.appointmentFilter}&status=pending`, context),
            }),
            waitlist: forkJoin({
                owned: this.getCount(`Appointment?_tag=${orgId}${dateRange.appointmentFilter}&status=waitlist`, context),
                shared: this.getCount(`Appointment?_security=${orgId}${dateRange.appointmentFilter}&status=waitlist`, context),
            }),
        };

        const currentStatusCountQueries: Record<keyof AppointmentStatusCounts, Observable<OwnedSharedCount>> = {
            proposed: forkJoin({
                owned: this.getCount(`Appointment?_tag=${orgId}&status=proposed`, context),
                shared: this.getCount(`Appointment?_security=${orgId}&status=proposed`, context),
            }),
            fulfilled: forkJoin({
                owned: this.getCount(`Appointment?_tag=${orgId}&status=fulfilled`, context),
                shared: this.getCount(`Appointment?_security=${orgId}&status=fulfilled`, context),
            }),
            noShow: forkJoin({
                owned: this.getCount(`Appointment?_tag=${orgId}&status=noshow`, context),
                shared: this.getCount(`Appointment?_security=${orgId}&status=noshow`, context),
            }),
            booked: forkJoin({
                owned: this.getCount(`Appointment?_tag=${orgId}&status=booked`, context),
                shared: this.getCount(`Appointment?_security=${orgId}&status=booked`, context),
            }),
            pending: forkJoin({
                owned: this.getCount(`Appointment?_tag=${orgId}&status=pending`, context),
                shared: this.getCount(`Appointment?_security=${orgId}&status=pending`, context),
            }),
            waitlist: forkJoin({
                owned: this.getCount(`Appointment?_tag=${orgId}&status=waitlist`, context),
                shared: this.getCount(`Appointment?_security=${orgId}&status=waitlist`, context),
            }),
        };

        // Encounter Queries
        const encounterQueries = {
            total: this.getCount(`Encounter?_tag=${orgId}${dateRange.encounterFilter}`, context),
            inpatient: this.getBundle(`Encounter?_tag=${orgId}${dateRange.encounterFilter}&status=in-progress&class=IMP&_count=1000`, context).pipe(
                map(bundle => this.extractBundleResources(bundle, 'Encounter').filter((encounter: any) => this.isActiveAdmissionEncounter(encounter)).length),
                catchError(() => of(0))
            ),
            outpatient: this.getCount(`Encounter?_tag=${orgId}${dateRange.encounterFilter}&class=AMB`, context),
            emergency: this.getCount(`Encounter?_tag=${orgId}${dateRange.encounterFilter}&class=EMR`, context),
            virtual: this.getCount(`Encounter?_tag=${orgId}${dateRange.encounterFilter}&class=VR`, context),
            home: this.getCount(`Encounter?_tag=${orgId}${dateRange.encounterFilter}&class=HH`, context),
        };

        // Patient Queries
        const patientQueries = {
            owned: this.getBundle(`Patient?_tag=${orgId}${dateRange.patientFilter}&_count=1000`, context),
            shared: this.getBundle(`Patient?_security=${orgId}${dateRange.patientFilter}&_count=1000`, context),
        };

        // Resource Queries
        const resourceQueries = {
            locationsOwned: this.utilityService.threeTierQueryAndSyncToStateOnce$<any>({
                resourceType: 'Location',
                urlSearchObject: {
                    _tag: orgId,
                    _elements: 'id,status,physicalType,name,description,operationalStatus'
                },
                tier1FollowNextLinks: true,
                tier1MaxPages: 100,
                context
            }).pipe(catchError(() => of([] as any[]))),
            locationsShared: this.utilityService.threeTierQueryAndSyncToStateOnce$<any>({
                resourceType: 'Location',
                urlSearchObject: {
                    _security: orgId,
                    _elements: 'id,status,physicalType,name,description,operationalStatus'
                },
                tier1FollowNextLinks: true,
                tier1MaxPages: 100,
                context
            }).pipe(catchError(() => of([] as any[]))),
            medications: this.utilityService.threeTierQueryAndSyncToStateOnce$<any>({
                resourceType: 'Medication',
                urlSearchObject: {
                    _tag: orgId,
                    _count: 1000
                },
                tier1FollowNextLinks: true,
                tier1MaxPages: 100,
                context
            }).pipe(catchError(() => of([] as any[])))
        };

        return forkJoin({
            appt: forkJoin(apptQueries),
            enc: forkJoin(encounterQueries),
            pat: forkJoin(patientQueries),
            statusCounts: forkJoin(statusCountQueries),
            currentStatusCounts: forkJoin(currentStatusCountQueries),
            resources: forkJoin(resourceQueries)
        }).pipe(
            map(results => {
                const statusCounts = results.statusCounts as AppointmentStatusCounts;
                const currentStatusCounts = results.currentStatusCounts as AppointmentStatusCounts;
                const appointmentsTotal = (results.appt.owned ?? 0) + (results.appt.shared ?? 0);
                const appointmentCounts = {
                    fulfilled: (statusCounts.fulfilled.owned ?? 0) + (statusCounts.fulfilled.shared ?? 0),
                    noShow: (statusCounts.noShow.owned ?? 0) + (statusCounts.noShow.shared ?? 0),
                    proposed: (statusCounts.proposed.owned ?? 0) + (statusCounts.proposed.shared ?? 0),
                    booked: (statusCounts.booked.owned ?? 0) + (statusCounts.booked.shared ?? 0),
                    pending: (statusCounts.pending.owned ?? 0) + (statusCounts.pending.shared ?? 0),
                    waitlist: (statusCounts.waitlist.owned ?? 0) + (statusCounts.waitlist.shared ?? 0),
                };
                const currentAppointmentCounts = {
                    proposed: (currentStatusCounts.proposed.owned ?? 0) + (currentStatusCounts.proposed.shared ?? 0),
                    booked: (currentStatusCounts.booked.owned ?? 0) + (currentStatusCounts.booked.shared ?? 0),
                    pending: (currentStatusCounts.pending.owned ?? 0) + (currentStatusCounts.pending.shared ?? 0),
                    waitlist: (currentStatusCounts.waitlist.owned ?? 0) + (currentStatusCounts.waitlist.shared ?? 0),
                };
                const patients = this.dedupeResources([
                    ...this.extractBundleResources(results.pat.owned, 'Patient'),
                    ...this.extractBundleResources(results.pat.shared, 'Patient')
                ]);
                const locOwned = Array.isArray(results.resources.locationsOwned) ? results.resources.locationsOwned : [];
                const locShared = Array.isArray(results.resources.locationsShared) ? results.resources.locationsShared : [];

                console.log(`[AdminDashboard] Raw fetch lengths - locOwned: ${locOwned.length}, locShared: ${locShared.length}`);

                const allLocations = this.dedupeResources([...locOwned, ...locShared]);
                const allBeds = allLocations.filter((location: any) => this.isBedLocation(location));

                const bedsTotal = allBeds.length;
                const bedsAvailable = allBeds.filter((loc: any) => this.isBedAvailable(loc)).length;
                
                console.log(`[AdminDashboard] Total beds: ${bedsTotal}, Available beds: ${bedsAvailable}`);

                const medications = Array.isArray(results.resources.medications)
                    ? results.resources.medications.filter((resource: any) => resource?.resourceType === 'Medication')
                    : []; 

                let outOfStockMedicines = 0;
                medications.forEach((med: any) => {
                    const stock = this.utilityService.getMedicationTotalRemaining(med);
                    if (stock && typeof stock.value === 'number' && stock.value <= 0) {
                        outOfStockMedicines++;
                    } else if (!stock) {
                        // If no stock extension is present, consider it out of stock if we treat lack of data as such
                        // or just skip. The user said "inventory is in an extension", so we'll only count if value <= 0.
                    }
                });

                console.warn('Admin dashboard metrics breakdown', {
                    timeRange,
                    dateRange,
                    appointments: {
                        total: appointmentsTotal,
                        ...appointmentCounts,
                        remaining: Math.max(appointmentCounts.booked - appointmentCounts.fulfilled, 0)
                    },
                    currentAppointments: currentAppointmentCounts,
                    encounters: results.enc,
                    patients: {
                        total: patients.length
                    },
                    beds: {
                        total: bedsTotal,
                        available: bedsAvailable
                    }
                });

                return {
                    appointments: {
                        total: appointmentsTotal,
                        fulfilled: appointmentCounts.fulfilled,
                        noShow: appointmentCounts.noShow,
                        proposed: appointmentCounts.proposed,
                        booked: appointmentCounts.booked,
                        pending: appointmentCounts.pending,
                        waitlist: appointmentCounts.waitlist,
                        remaining: Math.max(appointmentCounts.booked - appointmentCounts.fulfilled, 0),
                        currentProposed: currentAppointmentCounts.proposed,
                        currentBooked: currentAppointmentCounts.booked,
                        currentPending: currentAppointmentCounts.pending,
                        currentWaitlist: currentAppointmentCounts.waitlist
                    },
                    encounters: {
                        total: results.enc.total,
                        inpatient: results.enc.inpatient,
                        outpatient: results.enc.outpatient,
                        emergency: results.enc.emergency,
                        virtual: results.enc.virtual,
                        home: results.enc.home
                    },
                    patients: {
                        total: patients.length
                    },
                    resources: {
                        bedsAvailable,
                        bedsTotal,
                        outOfStockMedicines,
                        totalMedicines: medications.length // Changed from supplies.length to medications.length
                    }
                };
            })
        );
    }

    private getDateSearchRange(timeRange: string): DateSearchRange {
        const now = new Date();
        const start = new Date();
        start.setHours(0, 0, 0, 0);
        const end = new Date(start);

        if (timeRange === 'This Week') {
            const day = now.getDay();
            const diff = now.getDate() - day + (day === 0 ? -6 : 1); // Monday
            start.setDate(diff);
            end.setTime(start.getTime());
            end.setDate(start.getDate() + 7);
        } else if (timeRange === 'This month') {
            start.setDate(1);
            end.setTime(start.getTime());
            end.setMonth(start.getMonth() + 1);
        } else {
            end.setDate(start.getDate() + 1);
        }

        const startDate = this.toFhirDate(start);
        const endDate = this.toFhirDate(end);
        return {
            start: startDate,
            end: endDate,
            appointmentFilter: `&date=ge${startDate}&date=lt${endDate}`,
            encounterFilter: `&date=ge${startDate}&date=lt${endDate}`,
            patientFilter: `&_lastUpdated=ge${startDate}&_lastUpdated=lt${endDate}`
        };
    }

    private toFhirDate(date: Date): string {
        const year = date.getFullYear();
        const month = `${date.getMonth() + 1}`.padStart(2, '0');
        const day = `${date.getDate()}`.padStart(2, '0');
        return `${year}-${month}-${day}`;
    }

    private getCount(query: string, context: HttpContext): Observable<number> {
        const url = `${this.backendUrl}/${query}${query.includes('?') ? '&' : '?'}_summary=count`;
        return this.http.get<Bundle<any>>(url, { context }).pipe(
            map(bundle => bundle.total ?? 0),
            catchError(() => of(0))
        );
    }

    private getBundle(query: string, context: HttpContext): Observable<Bundle<any>> {
        return this.http.get<Bundle<any>>(`${this.backendUrl}/${query}`, { context }).pipe(
            catchError(() => of({ resourceType: 'Bundle', type: 'searchset', total: 0, entry: [] } as Bundle<any>))
        );
    }

    private extractBundleResources(bundle: Bundle<any>, resourceType?: string): any[] {
        const entries = Array.isArray(bundle?.entry) ? bundle.entry : [];

        return entries
            .filter((entry: any) => entry?.search?.mode !== 'outcome')
            .map((entry: any) => entry?.resource)
            .filter((resource: any) => {
                if (!resource || resource.resourceType === 'OperationOutcome') {
                    return false;
                }

                return resourceType ? resource.resourceType === resourceType : true;
            });
    }

    private dedupeResources(resources: any[]): any[] {
        const seen = new Set<string>();
        return resources.filter((resource: any) => {
            const key = resource?.id || `${resource?.resourceType ?? 'Resource'}:${JSON.stringify(resource)}`;
            if (seen.has(key)) {
                return false;
            }
            seen.add(key);
            return true;
        });
    }

    private countAppointmentsByStatus(appointments: any[]): {
        fulfilled: number;
        noShow: number;
        proposed: number;
        booked: number;
        pending: number;
        waitlist: number;
    } {
        return appointments.reduce((counts, appointment: any) => {
            const status = (appointment?.status ?? '').toString().toLowerCase();
            if (status === 'fulfilled') {
                counts.fulfilled++;
            } else if (status === 'noshow' || status === 'no-show') {
                counts.noShow++;
            } else if (status === 'proposed') {
                counts.proposed++;
            } else if (status === 'booked') {
                counts.booked++;
            } else if (status === 'pending') {
                counts.pending++;
            } else if (status === 'waitlist') {
                counts.waitlist++;
            }
            return counts;
        }, { fulfilled: 0, noShow: 0, proposed: 0, booked: 0, pending: 0, waitlist: 0 });
    }

    private isBedLocation(location: any): boolean {
        const physicalType = location?.physicalType;
        if (!physicalType) {
            return false;
        }

        const text = physicalType.text?.toLowerCase().trim();
        if (text === 'bed') return true;

        const codings = Array.isArray(physicalType.coding) ? physicalType.coding : [];
        return codings.some((coding: any) => {
            const code = coding?.code?.toLowerCase().trim();
            const display = coding?.display?.toLowerCase().trim();
            return code === 'bd' || code === 'bed' || display === 'bed';
        });
    }

    private isActiveAdmissionEncounter(encounter: any): boolean {
        return encounter?.status === 'in-progress' &&
            encounter?.class?.code === 'IMP' &&
            !!encounter?.hospitalization?.preAdmissionIdentifier?.value;
    }

    private isBedAvailable(location: any): boolean {
        // Must be active
        const status = (location?.status || '').toString().toLowerCase();
        if (status !== 'active') {
            return false;
        }

        const opStatus = location?.operationalStatus;
        if (!opStatus) {
            return true;
        }

        const codings = Array.isArray(opStatus.coding) ? opStatus.coding : [opStatus];
        
        const isUnavailable = codings.some((coding: any) => {
            const code = (coding?.code || '').toString().toUpperCase();
            const display = (coding?.display || '').toString().toLowerCase();
            
            return ['O', 'C', 'H', 'K', 'I'].includes(code) ||
                   code === 'OCCUPIED' || code === 'CLOSED' || code === 'HOUSEKEEPING' ||
                   display === 'occupied' || display === 'closed' ||
                   display === 'housekeeping' || display === 'contaminated' ||
                   display === 'isolated';
        });

        if (isUnavailable) {
            console.log('[isBedAvailable] Bed unavailable due to operational status:', { location, codings });
        }

        return !isUnavailable;
    }

    private getEmptyMetrics(): DashboardMetrics {
        return {
            appointments: { 
                total: 0, fulfilled: 0, noShow: 0, proposed: 0, booked: 0, pending: 0, waitlist: 0, remaining: 0, 
                currentProposed: 0, currentBooked: 0, currentPending: 0, currentWaitlist: 0 
            },
            encounters: { total: 0, inpatient: 0, outpatient: 0, emergency: 0, virtual: 0, home: 0 },
            patients: { total: 0 },
            resources: { bedsAvailable: 0, bedsTotal: 0, outOfStockMedicines: 0, totalMedicines: 0 }
        };
    }
}
